import type { PreparedEvmRecovery } from "./onchainRecoveryEvmPublish";
import { ethers } from "ethers";
import { requestPrivateFees } from "../utils/relayer-client";
import { encryptInboxNoteWithPublicKey } from "../utils/inbox-crypto";
import type { JoinSplitNote } from "../joinsplit/types";
import { flatFieldHex } from "./notes";
import { type PrivateFeeQuote, readFeeNote } from "./privateFees";

export const PRIVATE_DEPOSIT_INTERFACE = new ethers.Interface([
  "function depositWithPrivateFees((bytes32 tokenHash,bytes32[] commitments,uint256[] amounts,bytes[] proofs) data,(bytes32 intentHash,bytes32 termsHash,bytes32 nonce,uint64 issuedAt,uint64 deadline,uint8 kind) quote,bytes signature) payable",
  "function depositTokenWithPrivateFees(address token,(bytes32 tokenHash,bytes32[] commitments,uint256[] amounts,bytes[] proofs) data,(bytes32 intentHash,bytes32 termsHash,bytes32 nonce,uint64 issuedAt,uint64 deadline,uint8 kind) quote,bytes signature)",
]);
export async function preparePrivateDepositQuote(params: {
  amount: bigint;
  pool: string;
  chainId: number;
  treeIndex: number;
  tokenHash: string;
  token?: string;
  signal?: AbortSignal;
}) {
  const quote = await requestPrivateFees<PrivateFeeQuote>(
    "deposit-prepare",
    {
      grossAmount: params.amount.toString(),
      treeIndex: params.treeIndex,
      tokenHash: params.tokenHash,
      token: params.token ?? ethers.ZeroAddress,
    },
    {
      pool: params.pool,
      signal: params.signal,
      requestKey: ethers.hexlify(ethers.randomBytes(32)),
    },
  );
  if (
    quote.chainId !== params.chainId ||
    ethers.getAddress(quote.pool) !== ethers.getAddress(params.pool) ||
    ethers.getAddress(quote.request?.token ?? ethers.ZeroAddress) !==
      ethers.getAddress(params.token ?? ethers.ZeroAddress) ||
    BigInt(quote.amounts.grossAmount) !== params.amount ||
    BigInt(quote.amounts.recipientAmount) <= 0n ||
    BigInt(quote.amounts.recipientAmount) + BigInt(quote.amounts.totalFee) !==
      params.amount ||
    BigInt(quote.amounts.gasReimbursement) !== 0n
  )
    throw Error("Deposit fee quote changed. Please try again.");
  return quote;
}
export async function authorizePrivateDeposit(params: {
  quote: PrivateFeeQuote;
  note: JoinSplitNote;
  recipientMpk: bigint;
  inboxPublicKey: string;
  proof: string;
  recovery?: PreparedEvmRecovery | null;
  prove: (note: JoinSplitNote) => Promise<string>;
  signal?: AbortSignal;
}) {
  const fees = params.quote.feeOutputs
    .filter((n): n is Record<string, any> => n !== null)
    .map(readFeeNote);
  const notes = [params.note, ...fees];
  if (
    fees.some(
      (n) =>
        n.poolAddress?.toLowerCase() !==
          params.note.poolAddress?.toLowerCase() ||
        n.treeIndex !== params.note.treeIndex ||
        n.tokenHash !== params.note.tokenHash,
    )
  )
    throw Error("Deposit fee outputs belong to another pool.");
  const proofs = [params.proof];
  for (const fee of fees) proofs.push(await params.prove(fee));
  const deposit = {
    tokenHash: flatFieldHex(params.note.tokenHash),
    commitments: notes.map((n) => n.commitment),
    amounts: notes.map((n) => n.value.toString()),
    proofs,
  };
  const outputRecovery = [
    {
      role: "payment",
      commitment: params.note.commitment,
      recipientKey: ethers.sha256(
        ethers.toUtf8Bytes(flatFieldHex(params.recipientMpk)),
      ),
      envelope: await encryptInboxNoteWithPublicKey(
        params.note,
        params.inboxPublicKey,
      ),
    },
  ];
  const authorized = await requestPrivateFees<PrivateFeeQuote>(
    "deposit-authorize",
    {
      id: params.quote.id,
      deposit,
      outputRecovery,
      ...(params.recovery ? { recoveryRecords: params.recovery.records } : {}),
    },
    { pool: params.quote.pool, signal: params.signal },
  );
  if (!authorized.authorization)
    throw Error("Deposit authorization was not received.");
  const token = params.note.tokenData!.tokenAddress;
  const innerCall =
    token === ethers.ZeroAddress
      ? PRIVATE_DEPOSIT_INTERFACE.encodeFunctionData("depositWithPrivateFees", [
          deposit,
          authorized.authorization.quote,
          authorized.authorization.signature,
        ])
      : PRIVATE_DEPOSIT_INTERFACE.encodeFunctionData(
          "depositTokenWithPrivateFees",
          [
            token,
            deposit,
            authorized.authorization.quote,
            authorized.authorization.signature,
          ],
        );
  return params.recovery ? params.recovery.wrap(innerCall) : innerCall;
}
